import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { getDateISO } from './helpers';

// ── Reporting system context ──
// Manages report state, module layout, and period/profile selections

// ── Layout item (react-grid-layout compatible) ──

// ── Reporting context for dashboard grid layout ──

export interface GridLayoutItem {
  i: string; // matches reportModuleId
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  isResizable?: boolean;
}

// ── Context type ──

type ReportingContextType = {
  // Report-level state (shared across all modules)
  reportId: string | null;
  profiles: ReportProfile[];
  period: ReportPeriod;
  modules: ReportModule[];
  gridLayout: GridLayoutItem[];
  reportName: string;
  isLoadedFromTemplate: boolean;

  // Dirty tracking
  isDirty: boolean;
  snapshotState: () => void;

  // Setters
  setReportId: Dispatch<SetStateAction<string | null>>;
  setProfiles: Dispatch<SetStateAction<ReportProfile[]>>;
  setPeriod: Dispatch<SetStateAction<ReportPeriod>>;
  setModules: Dispatch<SetStateAction<ReportModule[]>>;
  setReportName: Dispatch<SetStateAction<string>>;
  setIsLoadedFromTemplate: Dispatch<SetStateAction<boolean>>;

  // Module operations
  addModule: (moduleType: ModuleType, moduleDefinitionId?: number, definition?: ModuleDefinition) => void;
  removeModule: (reportModuleId: string) => void;

  // Layout operations
  updateGridLayout: (layout: GridLayoutItem[]) => void;

  // Derived helpers
  profileIds: string[];
};

const ReportingContext = createContext<ReportingContextType | null>(null);

// ── Default period (last 7 days) ──
// Uses account timezone (user.timezone in minutes) to match the Calendar pattern.

const getDefaultPeriod = (timezoneOffsetMinutes = 0): ReportPeriod => ({
  startDate: getDateISO(timezoneOffsetMinutes, 7),
  endDate: getDateISO(timezoneOffsetMinutes, 1),
});

const GRID_COLS = 4;

// ── Module size defaults (fallback when no definition is provided) ──

const DEFAULT_MODULE_SIZE = { w: 2, h: 3 };

const getModuleConstraints = (definition?: ModuleDefinition) => ({
  minW: definition?.minSize?.width ?? 1,
  minH: definition?.minSize?.height ?? 2,
  maxW: definition?.maxSize?.width,
  maxH: definition?.maxSize?.height,
  isResizable: definition?.isResizable ?? true,
});

const clampModuleSize = (size: { w: number; h: number }, definition?: ModuleDefinition) => {
  const { minW, minH, maxW, maxH } = getModuleConstraints(definition);

  return {
    w: Math.min(Math.max(size.w, minW), maxW ?? Infinity),
    h: Math.min(Math.max(size.h, minH), maxH ?? Infinity),
  };
};

const getDefaultModuleSize = (definition?: ModuleDefinition) => {
  if (!definition) return DEFAULT_MODULE_SIZE;

  return clampModuleSize(
    {
      w: definition.defaultSize.width,
      h: definition.defaultSize.height,
    },
    definition
  );
};

const findBestPosition = (size: { w: number; h: number }, existingModules: ReportModule[]): { x: number; y: number } => {
  const bottomY = existingModules.reduce((max, m) => Math.max(max, m.position.y + m.position.h), 0);
  const rows = Array.from(new Set(existingModules.map((m) => m.position.y))).sort((a, b) => a - b);

  let result: { x: number; y: number } | null = null;

  rows.some((rowY) => {
    const rowModules = existingModules.filter((m) => m.position.y === rowY);
    const usedCols = rowModules.reduce((sum, m) => sum + m.position.w, 0);

    if (usedCols + size.w > GRID_COLS) return false;

    const occupied = new Set<number>();

    rowModules.forEach((m) => {
      Array.from({ length: m.position.w }, (_, i) => m.position.x + i).forEach((col) => occupied.add(col));
    });

    return Array.from({ length: GRID_COLS - size.w + 1 }, (_, x) => x).some((x) => {
      const fits = Array.from({ length: size.w }, (_, i) => i).every((i) => !occupied.has(x + i));

      if (fits) {
        result = { x, y: rowY };
      }

      return fits;
    });
  });

  return result ?? { x: 0, y: bottomY };
};

export const createReportModule = (
  moduleType: ModuleType,
  existingModules: ReportModule[],
  moduleDefinitionId?: number,
  definition?: ModuleDefinition
): ReportModule => {
  const size = getDefaultModuleSize(definition);
  const { x, y } = findBestPosition(size, existingModules);

  return {
    reportModuleId: `rm_${uuidv4()}`,
    moduleDefinitionId: moduleDefinitionId ?? 0,
    moduleType,
    customName: null,
    position: { x, y, w: size.w, h: size.h },
    config: {},
    chartType: definition?.defaultChartType,
  };
};

// ── Module min heights ──
export const getMinH = (moduleType: ModuleType, definition?: ModuleDefinition): number => definition?.minSize?.height ?? 2;

export const buildGridLayoutFromModules = (
  mods: ReportModule[],
  moduleDefinitions?: ModuleDefinition[],
  resetToDefaultSize = false
): GridLayoutItem[] =>
  mods
    .filter((m) => m?.position)
    .map((m) => {
      const definition = moduleDefinitions?.find((d) => d.id === m.moduleDefinitionId);
      const { minW, minH, maxW, maxH, isResizable } = getModuleConstraints(definition);

      let w: number;
      let h: number;

      if (resetToDefaultSize && definition?.defaultSize) {
        const defaultSize = getDefaultModuleSize(definition);

        w = defaultSize.w;
        h = defaultSize.h;
      } else {
        w = Math.min(Math.max(m.position.w, minW), maxW ?? Infinity);
        h = Math.min(Math.max(m.position.h, minH), maxH ?? Infinity);
      }

      return {
        i: m.reportModuleId,
        x: m.position.x,
        y: m.position.y,
        w,
        h,
        minW,
        minH,
        maxW: definition?.maxSize?.width,
        maxH: definition?.maxSize?.height,
        isResizable,
      };
    });

// ── Fingerprint (used for dirty tracking) ──

const buildFingerprint = (name: string, mods: ReportModule[], profs: ReportProfile[]): string =>
  JSON.stringify({
    name,
    modules: mods.map((m) => ({ id: m.reportModuleId, defId: m.moduleDefinitionId, pos: m.position })),
    profileIds: profs.map((p) => p.id),
  });

// ── Provider ──

export const ReportingProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('reports');
  const [{ user }] = useBridgeContext();
  const timezoneOffset = user?.timezone ?? 0;
  const [reportId, setReportId] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<ReportProfile[]>([]);
  const [period, setPeriod] = useState<ReportPeriod>(() => getDefaultPeriod(timezoneOffset));
  const [modules, setModules] = useState<ReportModule[]>([]);
  const [gridLayout, setGridLayout] = useState<GridLayoutItem[]>([]);
  const [reportName, setReportName] = useState(t('new_reports_untitled_report') as string);
  const [isLoadedFromTemplate, setIsLoadedFromTemplate] = useState(false);

  // Dirty tracking — snapshot captures the "clean" state after load / save
  const [snapshot, setSnapshot] = useState<string>('');

  const snapshotState = useCallback(() => {
    setSnapshot(buildFingerprint(reportName, modules, profiles));
  }, [reportName, modules, profiles]);

  const isDirty = useMemo(() => {
    // Template-loaded reports are always dirty until saved
    if (isLoadedFromTemplate) return true;

    if (!snapshot) return false;

    return snapshot !== buildFingerprint(reportName, modules, profiles);
  }, [isLoadedFromTemplate, reportName, modules, profiles, snapshot]);

  const profileIds = useMemo(() => profiles.map((p) => p.id), [profiles]);

  const addModule = useCallback((moduleType: ModuleType, moduleDefinitionId?: number, definition?: ModuleDefinition) => {
    setModules((prev) => {
      const newModule = createReportModule(moduleType, prev, moduleDefinitionId, definition);
      const next = [...prev, newModule];

      setGridLayout((currentLayout) => {
        const newLayout = buildGridLayoutFromModules(next);

        return newLayout.map((item) => {
          const existing = currentLayout.find((l) => l.i === item.i);

          if (existing) {
            return {
              ...item,
              minW: existing.minW,
              minH: existing.minH,
              maxW: existing.maxW,
              maxH: existing.maxH,
              isResizable: existing.isResizable,
            };
          }

          const constraints = getModuleConstraints(definition);
          const clampedW = Math.min(Math.max(item.w, constraints.minW), constraints.maxW ?? Infinity);
          const clampedH = Math.min(Math.max(item.h, constraints.minH), constraints.maxH ?? Infinity);

          return { ...item, ...constraints, w: clampedW, h: clampedH };
        });
      });

      return next;
    });
  }, []);

  const removeModule = useCallback((reportModuleId: string) => {
    setModules((prev) => {
      const next = prev.filter((m) => m.reportModuleId !== reportModuleId);

      setGridLayout((currentLayout) => {
        const newLayout = buildGridLayoutFromModules(next);

        return newLayout.map((item) => {
          const existing = currentLayout.find((l) => l.i === item.i);

          return existing
            ? {
                ...item,
                minW: existing.minW,
                minH: existing.minH,
                maxW: existing.maxW,
                maxH: existing.maxH,
                isResizable: existing.isResizable,
              }
            : item;
        });
      });

      return next;
    });
  }, []);

  const updateGridLayout = useCallback((layout: GridLayoutItem[]) => {
    setGridLayout(layout);

    // Sync positions back to modules so they can be persisted
    setModules((prev) =>
      prev.map((m) => {
        const layoutItem = layout.find((l) => l.i === m.reportModuleId);

        if (!layoutItem) return m;

        return {
          ...m,
          position: { x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h },
        };
      })
    );
  }, []);

  const value = useMemo(
    () => ({
      reportId,
      profiles,
      period,
      modules,
      gridLayout,
      reportName,
      isLoadedFromTemplate,
      isDirty,
      snapshotState,
      setReportId,
      setProfiles,
      setPeriod,
      setModules,
      setReportName,
      setIsLoadedFromTemplate,
      addModule,
      removeModule,
      updateGridLayout,
      profileIds,
    }),
    [
      reportId,
      profiles,
      period,
      modules,
      gridLayout,
      reportName,
      isLoadedFromTemplate,
      isDirty,
      snapshotState,
      profileIds,
      addModule,
      removeModule,
      updateGridLayout,
    ]
  );

  return <ReportingContext.Provider value={value}>{children}</ReportingContext.Provider>;
};

// ── Hook ──

export const useReportingContext = (): ReportingContextType => {
  const context = useContext(ReportingContext);

  if (context === null) {
    throw new Error('useReportingContext must be used within a ReportingProvider');
  }

  return context;
};
